import { useEffect, useState } from 'react';
import { Loader2, Mail, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuthState, useRequestOtp, useVerifyOtp } from '../../hooks/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export function LoginForm() {
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const navigate = useNavigate();

  const { isAuthenticated } = useAuthState();
  const requestOtpMutation = useRequestOtp();
  const verifyOtpMutation = useVerifyOtp();

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    requestOtpMutation.mutate(
      { email },
      {
        onSuccess: () => {
          setStep('otp');
        },
      },
    );
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp) return;

    verifyOtpMutation.mutate(
      { email, otp },
      {
        onSuccess: () => {
          navigate('/', { replace: true });
        },
      },
    );
  };

  const handleBack = () => {
    setStep('email');
    setOtp('');
    requestOtpMutation.reset();
    verifyOtpMutation.reset();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-center text-2xl">Admin Login</CardTitle>
          <CardDescription className="text-center">
            {step === 'email' ? 'Enter your email to receive an OTP' : 'Enter the OTP sent to your email'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'email' ? (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-text-secondary" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {requestOtpMutation.error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{requestOtpMutation.error.message}</div>
              )}

              <Button type="submit" className="w-full" disabled={requestOtpMutation.isPending || !email}>
                {requestOtpMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">OTP Code</Label>
                <div className="relative">
                  <Shield className="absolute left-3 top-3 h-4 w-4 text-text-secondary" />
                  <Input
                    id="otp"
                    type="text"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="pl-10 text-center text-lg tracking-widest"
                    maxLength={6}
                    required
                  />
                </div>
                <p className="text-xs text-text-secondary">OTP sent to {email}</p>
              </div>

              {verifyOtpMutation.error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{verifyOtpMutation.error.message}</div>
              )}

              <div className="space-y-2">
                <Button type="submit" className="w-full" disabled={verifyOtpMutation.isPending || !otp}>
                  {verifyOtpMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Login'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleBack}
                  disabled={verifyOtpMutation.isPending}
                >
                  Back to Email
                </Button>
              </div>
            </form>
          )}

          {step === 'otp' && (
            <div className="mt-4 rounded-md bg-muted p-3">
              <p className="text-xs text-text-secondary">
                Please check your email for the OTP code. The code will expire in 10 minutes.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
